"use client"

import {
  type ComponentPropsWithoutRef,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaTimeDisplay,
  MediaTimeRange,
} from "media-chrome/react"
import VimeoVideo from "vimeo-video-element/react"
import YoutubeVideo from "youtube-video-element/react"

import type { VideoInterface } from "~/components/modules/Video/Video.interface"
import { Asset } from "~/components/primitives/Asset/Asset"
import { useVideoContext } from "~/contexts/VideoContext"
import { getVideoProvider } from "~/functions/getVideoProvider"

import s from "./VideoPlayer.module.scss"

type Props = Partial<VideoInterface> & {
  moduleId: string
} & Omit<
    ComponentPropsWithoutRef<typeof MediaController>,
    keyof VideoInterface | "moduleIndex" | "moduleId"
  >

export const VideoPlayer = ({
  moduleId,
  thumbnailVideo,
  posterImage,
  videoSource,
  ...rest
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { activeVideoId, playVideo } = useVideoContext()

  const [played, setPlayed] = useState(false)

  const pauseMedia = () => {
    const media = containerRef.current?.querySelector(
      "video, youtube-video, vimeo-video",
    ) as HTMLMediaElement | null
    media?.pause()
  }

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting) pauseMedia()
      },
      { threshold: 0 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!activeVideoId || activeVideoId === moduleId) return
    pauseMedia()
  }, [activeVideoId, moduleId])

  const handlePlayed = () => {
    if (!played) setPlayed(true)
    playVideo(moduleId)
  }

  const embedPoster =
    videoSource?.kind === "json" && videoSource?.poster
      ? videoSource?.poster
      : null

  // A single provider, so a .mp4 served from player.vimeo.com can't mount both
  // a native <video> and a <vimeo-video> into the same slot
  const provider = getVideoProvider(videoSource)

  return (
    <div className={s.videoPlayer} ref={containerRef}>
      <MediaController {...rest}>
        {!played && (
          <div className={s.thumbnailWrapper}>
            {thumbnailVideo && (
              <Asset
                asset={thumbnailVideo}
                objectFit="cover"
                className={s.thumbnail}
                imageSizes={{ base: "80vw", md: "60vw" }}
                videoProps={{
                  autoPlay: true,
                  muted: true,
                  loop: true,
                  playsInline: true,
                }}
              />
            )}
            {posterImage && !thumbnailVideo && (
              <Asset
                asset={posterImage}
                objectFit="cover"
                className={s.thumbnail}
                imageSizes={{ base: "80vw", md: "60vw" }}
              />
            )}
            {embedPoster && !thumbnailVideo && !posterImage && (
              <Asset
                asset={embedPoster}
                objectFit="cover"
                className={s.thumbnail}
                imageSizes={{ base: "80vw", md: "60vw" }}
              />
            )}
          </div>
        )}
        {/* media-controller sets tabindex="-1" on its slotted media once it
            upgrades, which can happen before hydration — suppress the
            resulting attribute-mismatch warning on the media elements */}
        {provider === "file" && (
          <video
            slot="media"
            src={videoSource?.url}
            onPlay={handlePlayed}
            suppressHydrationWarning
          >
            <track kind="captions" />
          </video>
        )}
        {provider === "youtube" && (
          <YoutubeVideo
            slot="media"
            src={videoSource?.url}
            onPlay={handlePlayed}
            suppressHydrationWarning
          />
        )}
        {provider === "vimeo" && (
          <VimeoVideo
            slot="media"
            src={videoSource?.url}
            onPlay={handlePlayed}
            suppressHydrationWarning
          />
        )}

        <MediaControlBar className={s.controlBar}>
          {played ? (
            <>
              <MediaPlayButton></MediaPlayButton>
              <MediaMuteButton></MediaMuteButton>
              <MediaTimeRange></MediaTimeRange>
              <MediaTimeDisplay></MediaTimeDisplay>
              <MediaFullscreenButton></MediaFullscreenButton>
            </>
          ) : (
            <MediaPlayButton className={s.playButton}>
              <div slot="play" className={s.playIconWrapper}>
                <svg
                  className={s.playIcon}
                  viewBox="0 0 98 110"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M92.8928 46.0547C94.4187 46.9307 95.6865 48.1938 96.5682 49.7165C97.4498 51.2393 97.9141 52.9676 97.9141 54.7272C97.9141 56.4867 97.4498 58.2151 96.5682 59.7378C95.6865 61.2605 94.4187 62.5236 92.8928 63.3997L14.9788 108.128C13.4584 109.001 11.7352 109.458 9.98196 109.455C8.22876 109.452 6.50719 108.988 4.98988 108.11C3.47257 107.231 2.21284 105.97 1.33701 104.451C0.461182 102.932 3.05176e-05 101.21 -0.000205994 99.4566V9.99866C3.05176e-05 8.24546 0.461182 6.52313 1.33701 5.00437C2.21284 3.48562 3.47257 2.22381 4.98988 1.34547C6.50719 0.467137 8.22876 0.00314321 9.98196 1.59136e-05C11.7352 -0.00311138 13.4584 0.454738 14.9788 1.32766L92.8928 46.0547Z"
                    fill="white"
                  />
                </svg>
              </div>
            </MediaPlayButton>
          )}
        </MediaControlBar>
      </MediaController>
    </div>
  )
}
